import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { eventApi, paymentApi } from '../services/api';
import { toast } from 'sonner';
import { Calendar, Star, Clock, CheckCircle, XCircle, AlertCircle, Plus } from 'lucide-react';

const CommunityAccount = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isCommunityMember, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || !isCommunityMember) {
      navigate('/community-login');
      return;
    }
    loadData();
    
    // Check for payment success
    const sessionId = searchParams.get('session_id');
    if (sessionId && searchParams.get('payment') === 'success') {
      pollPaymentStatus(sessionId);
    }
  }, [isAuthenticated, isCommunityMember, authLoading, navigate, searchParams]);

  const loadData = async () => {
    try {
      const [eventsRes, paymentsRes] = await Promise.all([
        eventApi.getMine(),
        paymentApi.getHistory()
      ]);
      setEvents(eventsRes.data);
      setPayments(paymentsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      toast.info('Payment is being processed. Please check back soon.');
      return;
    }
    
    try {
      const response = await paymentApi.getStatus(sessionId);
      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful! Your event will be featured once approved.');
        loadData();
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error('Error checking payment:', error);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <span className="badge badge-status-approved flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Approved</span>;
      case 'rejected':
        return <span className="badge badge-status-rejected flex items-center gap-1"><XCircle className="w-3 h-3" /> Rejected</span>;
      default:
        return <span className="badge badge-status-pending flex items-center gap-1"><Clock className="w-3 h-3" /> Pending</span>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 py-8" data-testid="community-account">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-neutral-900 mb-2" style={{ fontFamily: 'DM Serif Display, serif' }}>
            Welcome, {user?.name}!
          </h1>
          <p className="text-neutral-600">Community Member Dashboard</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* My Events */}
            <div className="dashboard-card">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-neutral-900">My Events</h2>
                <Link to="/submit-event" className="btn-primary text-sm flex items-center gap-2">
                  <Plus className="w-4 h-4" /> Submit Event
                </Link>
              </div>

              {events.length > 0 ? (
                <div className="space-y-4">
                  {(() => {
                    const today = new Date().toISOString().split('T')[0];
                    const upcoming = events.filter(e => (e.end_date || e.event_date) >= today);
                    const past = events.filter(e => (e.end_date || e.event_date) < today);
                    return (
                      <>
                        {upcoming.map((event) => (
                          <div key={event.id} className="flex items-start gap-4 p-4 bg-neutral-50 rounded-xl">
                            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                              <Calendar className="w-6 h-6 text-primary" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2 mb-1">
                                <h3 className="font-semibold text-neutral-900 truncate">{event.event_name}</h3>
                                {getStatusBadge(event.status)}
                              </div>
                              <p className="text-sm text-neutral-600 mb-2">{event.event_date}{event.end_date && event.end_date !== event.event_date ? ` - ${event.end_date}` : ''} &bull; {event.category}</p>
                              {event.is_featured && (
                                <span className="badge badge-featured text-xs flex items-center gap-1 w-fit">
                                  <Star className="w-3 h-3" /> Featured
                                </span>
                              )}
                              {event.status === 'approved' && !event.is_featured && (
                                <Link to={`/feature-my-event?event=${event.id}`} className="text-sm text-accent font-semibold hover:underline">
                                  Feature this event &rarr;
                                </Link>
                              )}
                            </div>
                          </div>
                        ))}
                        {past.length > 0 && (
                          <>
                            <h3 className="text-sm font-semibold text-neutral-400 uppercase tracking-wide pt-4 border-t border-neutral-200">Past Events</h3>
                            {past.map((event) => (
                              <div key={event.id} className="flex items-start gap-4 p-4 bg-neutral-100/50 rounded-xl opacity-60">
                                <div className="w-12 h-12 rounded-lg bg-neutral-200 flex items-center justify-center flex-shrink-0">
                                  <Calendar className="w-6 h-6 text-neutral-400" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-2 mb-1">
                                    <h3 className="font-semibold text-neutral-600 truncate">{event.event_name}</h3>
                                    <span className="badge text-xs bg-neutral-200 text-neutral-500">Ended</span>
                                  </div>
                                  <p className="text-sm text-neutral-500">{event.event_date}{event.end_date && event.end_date !== event.event_date ? ` - ${event.end_date}` : ''} &bull; {event.category}</p>
                                </div>
                              </div>
                            ))}
                          </>
                        )}
                      </>
                    );
                  })()}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
                  <p className="text-neutral-600 mb-4">No events submitted yet</p>
                  <Link to="/submit-event" className="btn-primary">
                    Submit Your First Event
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="dashboard-card">
              <h3 className="font-bold text-lg mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <Link to="/submit-event" className="block p-4 bg-neutral-50 rounded-xl hover:bg-neutral-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-primary" />
                    <span className="font-medium">Submit New Event</span>
                  </div>
                </Link>
                <Link to="/events" className="block p-4 bg-neutral-50 rounded-xl hover:bg-neutral-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-secondary" />
                    <span className="font-medium">Browse Events</span>
                  </div>
                </Link>
              </div>
            </div>

            {/* Payment History */}
            <div className="dashboard-card">
              <h3 className="font-bold text-lg mb-4">Featured Payments</h3>
              {payments.filter(p => p.payment_status === 'paid').length > 0 ? (
                <div className="space-y-3">
                  {payments.filter(p => p.payment_status === 'paid').slice(0, 5).map((payment) => (
                    <div key={payment.id} className="flex items-center justify-between py-2 border-b border-neutral-100 last:border-0">
                      <div>
                        <p className="font-medium text-sm">{payment.metadata?.product_name || payment.product_type}</p>
                        <p className="text-xs text-neutral-500">{new Date(payment.created_at).toLocaleDateString()}</p>
                      </div>
                      <span className="font-semibold text-neutral-900">${payment.amount}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-neutral-500 text-sm">No payments yet</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunityAccount;
